import React, { useEffect } from 'react';
import { Button, Drawer, Select, Table, message } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { QUERY_PICKUP_RESERVATION_DETAIL } from '../../../graphql/queries/dashboard/queryPickupReservationDetail';
import {
    QueryPickupReservationDetailResponse,
    PickupReservationDetailItem,
} from '../../../graphql/types/dashboard/PickupDashboardTypes';
import { GENERATE_PICKUP_RESERVATION_DETAIL_DOWNLOAD } from '../../../graphql/mutations/dashboard/generatePickupReservationDetailDownloadReport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';

interface PickupReservationDrawerProps {
    visible: boolean;
    onClose: () => void;
    startDate: string;
    endDate: string;
    hubIds?: number[];
    customerIds?: number[];
    ewrType?: string;
    bizDate?: string;
    drillFilter?: Record<string, number>;
}

const PAGE_SIZE = 20;

const PickupReservationDrawer: React.FC<PickupReservationDrawerProps> = ({
    visible,
    onClose,
    startDate,
    endDate,
    hubIds,
    customerIds,
    ewrType,
    bizDate,
    drillFilter,
}) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = React.useState(1);
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [downloadModalVisible, setDownloadModalVisible] = React.useState(false);
    const [downloadFileName, setDownloadFileName] = React.useState('');

    // Filter states
    const [filterReservatConfirmStatus, setFilterReservatConfirmStatus] = React.useState<string | undefined>(undefined);
    const [filterReservatStatusList, setFilterReservatStatusList] = React.useState<string[]>([]);
    const [filterReservatSourceList, setFilterReservatSourceList] = React.useState<string[]>([]);
    const [filterIsWiring, setFilterIsWiring] = React.useState<string | undefined>(undefined);
    const [filterIsVisit, setFilterIsVisit] = React.useState<string | undefined>(undefined);
    const [filterVisitResult, setFilterVisitResult] = React.useState<string | undefined>(undefined);
    const [filterIsTemu, setFilterIsTemu] = React.useState<number | undefined>(undefined);
    const [filterIsExpected, setFilterIsExpected] = React.useState<number | undefined>(undefined);

    const { effectiveStart, effectiveEnd } = React.useMemo(() => {
        const capEnd = (e: string) => e > endDate ? endDate : e;
        if (!bizDate) return { effectiveStart: startDate, effectiveEnd: endDate };
        if (bizDate.includes('~')) {
            const [s, e] = bizDate.split('~');
            return { effectiveStart: s.trim(), effectiveEnd: capEnd(e.trim()) };
        }
        if (/^\d{4}-\d{2}$/.test(bizDate)) {
            const start = bizDate + '-01';
            const end = dayjs(`${bizDate}-01`).endOf('month').format('YYYY-MM-DD');
            return { effectiveStart: start, effectiveEnd: capEnd(end) };
        }
        return { effectiveStart: bizDate, effectiveEnd: bizDate };
    }, [bizDate, startDate, endDate]);

    // 构建当前筛选条件（直接引用最新 state，不用 useCallback 避免 stale closure）
    const buildInput = (page: number) => ({
        startDate: effectiveStart,
        endDate: effectiveEnd,
        hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
        customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
        ewrType: ewrType ?? undefined,
        page,
        pageSize: PAGE_SIZE,
        isShouldCollect: drillFilter?.isShouldCollect ?? undefined,
        reservatConfirmStatus: filterReservatConfirmStatus ?? undefined,
        reservatStatusList: filterReservatStatusList.length > 0 ? filterReservatStatusList : undefined,
        reservatSourceList: filterReservatSourceList.length > 0 ? filterReservatSourceList : undefined,
        isWiring: filterIsWiring ?? undefined,
        isVisit: filterIsVisit ?? undefined,
        visitResult: filterVisitResult ?? undefined,
        isTemu: filterIsTemu ?? undefined,
        isExpected: filterIsExpected ?? undefined,
    });

    const [fetchDetail, { data, loading }] = useLazyQuery<QueryPickupReservationDetailResponse>(
        QUERY_PICKUP_RESERVATION_DETAIL
    );

    const [generateReport] = useMutation(GENERATE_PICKUP_RESERVATION_DETAIL_DOWNLOAD, {
        onCompleted: (resData) => {
            const fileName = resData.generatePickupReservationDetailDownloadReport;
            if (fileName) {
                setDownloadFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                void message.error('导出失败，请稍后重试');
            }
        },
        onError: () => { void message.error('导出失败，请稍后重试'); },
    });

    // visible 变为 true 时：重置所有筛选框，并以空筛选条件发起请求
    useEffect(() => {
        if (!visible) return;
        setCurrentPage(1);
        setFilterReservatConfirmStatus(undefined);
        setFilterReservatStatusList([]);
        setFilterReservatSourceList([]);
        setFilterIsWiring(undefined);
        setFilterIsVisit(undefined);
        setFilterVisitResult(undefined);
        setFilterIsTemu(undefined);
        setFilterIsExpected(undefined);
        fetchDetail({
            variables: {
                input: {
                    startDate: effectiveStart,
                    endDate: effectiveEnd,
                    hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
                    customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
                    ewrType: ewrType ?? undefined,
                    page: 1,
                    pageSize: PAGE_SIZE,
                    isShouldCollect: drillFilter?.isShouldCollect ?? undefined,
                },
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, effectiveStart, effectiveEnd, hubIds, customerIds, ewrType, drillFilter]);

    // 筛选条件变化时重新请求
    useEffect(() => {
        if (!visible) return;
        setCurrentPage(1);
        fetchDetail({ variables: { input: buildInput(1) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterReservatConfirmStatus, filterReservatStatusList, filterReservatSourceList,
        filterIsWiring, filterIsVisit, filterVisitResult, filterIsTemu, filterIsExpected]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchDetail({ variables: { input: buildInput(page) } });
    };

    const handleDownload = () => {
        void runExportWithCooldown(async () => {
            await generateReport({
                variables: {
                    input: buildInput(1),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            });
        });
    };

    const result = data?.queryPickupReservationDetail;
    const dataSource = result?.content ?? [];
    const total = result?.total ?? 0;

    const formatUnit = (unit: string | null | undefined) => {
        if (!unit) return '--';
        const map: Record<string, string> = {
            PIECE: t('enum.packageUnitFilterOptions.piece') as string,
            BAG:   t('enum.packageUnitFilterOptions.bag') as string,
            CARTON: t('enum.packageUnitFilterOptions.carton') as string,
            PALLET: t('enum.packageUnitFilterOptions.pallet') as string,
        };
        return map[unit] ?? unit;
    };

    const formatStatus = (status: string | null | undefined) => {
        if (!status) return '-';
        const map: Record<string, string> = {
            CREATED:       t('enum.pickupAppointmentStatus.created') as string,
            DISPATCHING:   t('enum.pickupAppointmentStatus.dispatching') as string,
            DISPATCHED:    t('enum.pickupAppointmentStatus.dispatched') as string,
            PICKING_UP:    t('enum.pickupAppointmentStatus.pickingUp') as string,
            PICKED_UP:     t('enum.pickupAppointmentStatus.pickedUp') as string,
            PICKUP_FAILED: t('enum.pickupAppointmentStatus.pickupFailed') as string,
            CANCELLED:     t('enum.pickupAppointmentStatus.cancelled') as string,
        };
        return map[status] ?? status;
    };

    const formatSource = (source: string | null | undefined) => {
        if (!source) return '-';
        const map: Record<string, string> = {
            STANDING_PICKUP: t('enum.pickupAppointmentSource.standingPickup') as string,
            MANUAL:          t('enum.pickupAppointmentSource.manual') as string,
            SYSTEM_SLA:      t('enum.pickupAppointmentSource.systemSla') as string,
            SYSTEM:          t('enum.pickupAppointmentSource.system') as string,
            COPY:            t('enum.pickupAppointmentSource.copy') as string,
        };
        return map[source] ?? source;
    };

    const columns: ColumnsType<PickupReservationDetailItem> = [
        { title: t('dashboard.pickup.drawer.reservationNo'), dataIndex: 'reservationNo', key: 'reservationNo', width: 160, fixed: 'left' },
        { title: '预约单确认状态', dataIndex: 'reservatConfirmStatus', key: 'reservatConfirmStatus', width: 130, render: (v: string) => v || '-' },
        { title: '预约单状态', dataIndex: 'reservatStatus', key: 'reservatStatus', width: 120, render: (v: string) => v || '-' },
        { title: '预约单来源', dataIndex: 'reservatSource', key: 'reservatSource', width: 120, render: (v: string) => v || '-' },
        { title: '排线状态', dataIndex: 'isWiring', key: 'isWiring', width: 100, render: (v: string) => v || '-' },
        { title: '上门状态', dataIndex: 'isVisit', key: 'isVisit', width: 100, render: (v: string) => v || '-' },
        { title: '上门结果', dataIndex: 'visitResult', key: 'visitResult', width: 100, render: (v: string) => v || '-' },
        { title: '是否TEMU认证仓', dataIndex: 'isTemu', key: 'isTemu', width: 130, render: (v: number) => (v === 1 ? '是' : '否') },
        { title: '是否有预计提货时间', dataIndex: 'isExpected', key: 'isExpected', width: 150, render: (v: number) => (v === 1 ? '是' : '否') },
        { title: t('dashboard.pickup.drawer.hub'), dataIndex: 'hubName', key: 'hubName', width: 120, render: (v: string | null) => v || '--' },
        { title: t('dashboard.pickup.drawer.siteId'), dataIndex: 'siteId', key: 'siteId', width: 100, render: (v: number | null) => v || '--' },
        { title: t('dashboard.pickup.drawer.siteName'), dataIndex: 'siteName', key: 'siteName', width: 160, render: (v: string | null) => v || '--' },
        { title: t('dashboard.pickup.drawer.vehicleType'), dataIndex: 'vehicleType', key: 'vehicleType', width: 110 },
        {
            title: t('dashboard.pickup.drawer.contactPhone'),
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 150,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
        },
        { title: t('dashboard.pickup.drawer.customerName'), dataIndex: 'customerName', key: 'customerName', width: 140, render: (v: string | null) => v || '--' },
        { title: t('dashboard.pickup.drawer.address'), dataIndex: 'address', key: 'address', width: 200 },
        {
            title: t('dashboard.pickup.drawer.source'),
            dataIndex: 'source',
            key: 'source',
            width: 130,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{formatSource(v)}</span>,
        },
        { title: t('dashboard.pickup.drawer.continuousFailure'), dataIndex: 'continuousFailure', key: 'continuousFailure', width: 100, render: (v: boolean | null) => v === null || v === undefined ? '-' : (v ? '是' : '否') },
        {
            title: t('dashboard.pickup.drawer.routeId'),
            dataIndex: 'routeName',
            key: 'routeName',
            width: 120,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
        },
        { title: t('dashboard.pickup.drawer.zipcode'), dataIndex: 'zipcode', key: 'zipcode', width: 100 },
        {
            title: t('dashboard.pickup.drawer.expectedPickupDate'),
            dataIndex: 'expectedPickupDate',
            key: 'expectedPickupDate',
            width: 190,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '--'}</span>,
        },
        {
            title: t('dashboard.pickup.drawer.timezone'),
            dataIndex: 'timezone',
            key: 'timezone',
            width: 130,
            render: (v: string | null) => v || '--',
        },
        {
            title: t('dashboard.pickup.drawer.expectedParcelCount'),
            dataIndex: 'expectedParcelCount',
            key: 'expectedParcelCount',
            width: 110,
            render: (v: number) => v || '--',
        },
        {
            title: t('dashboard.pickup.drawer.expectedParcelUnit'),
            dataIndex: 'expectedParcelUnit',
            key: 'expectedParcelUnit',
            width: 120,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{formatUnit(v)}</span>,
        },
        {
            title: t('dashboard.pickup.drawer.actualParcelCount'),
            dataIndex: 'actualParcelCount',
            key: 'actualParcelCount',
            width: 110,
            render: (v: number) => v || '--',
        },
        {
            title: t('dashboard.pickup.drawer.actualParcelUnit'),
            dataIndex: 'actualParcelUnit',
            key: 'actualParcelUnit',
            width: 110,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{formatUnit(v)}</span>,
        },
        {
            title: t('pickup.appointment.columns.parcels'),
            dataIndex: 'expectedScanParcelCount',
            key: 'expectedScanParcelCount',
            width: 120,
            render: (v: number) => <span>{v ?? '--'}</span>,
        },
        {
            title: t('pickup.appointment.columns.actualScanCount'),
            dataIndex: 'actualScanParcelCount',
            key: 'actualScanParcelCount',
            width: 140,
            render: (v: number) => <span>{v || '--'}</span>,
        },
        { title: t('dashboard.pickup.drawer.collectFailReason'), dataIndex: 'collectFailReason', key: 'collectFailReason', width: 150 },
        {
            title: t('dashboard.pickup.drawer.taskId'),
            dataIndex: 'taskNumber',
            key: 'taskNumber',
            width: 130,
            render: (v: string | null) => v || '--',
        },
        {
            title: t('dashboard.pickup.drawer.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 170,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v ? dayjs(v).format('YYYY/MM/DD HH:mm:ss') : '--'}</span>,
        },
    ];

    const drawerTitle = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
                {t('dashboard.pickup.drawer.reservationTitle')}
                {effectiveStart && `（${effectiveStart}${effectiveStart !== effectiveEnd ? ` ~ ${effectiveEnd}` : ''}）`}
            </span>
            <Button
                type="primary"
                size="small"
                style={{ marginRight: 32 }}
                loading={exportLoading}
                disabled={exportLoading}
                onClick={handleDownload}
            >
                {getExportButtonLabel('下载')}
            </Button>
        </div>
    );

    return (
        <Drawer
            title={drawerTitle}
            open={visible}
            onClose={onClose}
            width="90vw"
            destroyOnClose
        >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <Select
                    allowClear
                    placeholder="预约单确认状态"
                    style={{ width: 160 }}
                    value={filterReservatConfirmStatus}
                    onChange={setFilterReservatConfirmStatus}
                    options={[
                        { value: '待确认', label: '待确认' },
                        { value: '确认',   label: '确认' },
                    ]}
                />
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="预约单状态"
                    style={{ minWidth: 160 }}
                    value={filterReservatStatusList}
                    onChange={setFilterReservatStatusList}
                    maxTagCount="responsive"
                    options={[
                        { value: '已创建',  label: '已创建' },
                        { value: '调度中',  label: '调度中' },
                        { value: '已调度',  label: '已调度' },
                        { value: '揽收中',  label: '揽收中' },
                        { value: '已揽收',  label: '已揽收' },
                        { value: '揽收失败', label: '揽收失败' },
                        { value: '已取消',  label: '已取消' },
                    ]}
                />
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="预约单来源"
                    style={{ minWidth: 160 }}
                    value={filterReservatSourceList}
                    onChange={setFilterReservatSourceList}
                    maxTagCount="responsive"
                    options={[
                        { value: '长期揽收', label: '长期揽收' },
                        { value: '手动创建', label: '手动创建' },
                        { value: '系统创建', label: '系统创建' },
                        { value: '复制',    label: '复制' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="排线状态"
                    style={{ width: 160 }}
                    value={filterIsWiring}
                    onChange={setFilterIsWiring}
                    options={[
                        { value: '未排线', label: '未排线' },
                        { value: '已排线', label: '已排线' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="上门状态"
                    style={{ width: 160 }}
                    value={filterIsVisit}
                    onChange={setFilterIsVisit}
                    options={[
                        { value: '未上门', label: '未上门' },
                        { value: '已上门', label: '已上门' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="上门结果"
                    style={{ width: 160 }}
                    value={filterVisitResult}
                    onChange={setFilterVisitResult}
                    options={[
                        { value: '揽收失败', label: '揽收失败' },
                        { value: '揽收成功', label: '揽收成功' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="是否TEMU认证仓"
                    style={{ width: 160 }}
                    value={filterIsTemu}
                    onChange={setFilterIsTemu}
                    options={[
                        { value: 0, label: '否' },
                        { value: 1, label: '是' },
                    ]}
                />
                <Select
                    allowClear
                    placeholder="是否有预计提货时间"
                    style={{ width: 160 }}
                    value={filterIsExpected}
                    onChange={setFilterIsExpected}
                    options={[
                        { value: 0, label: '否' },
                        { value: 1, label: '是' },
                    ]}
                />
                <Button
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                        setFilterReservatConfirmStatus(undefined);
                        setFilterReservatStatusList([]);
                        setFilterReservatSourceList([]);
                        setFilterIsWiring(undefined);
                        setFilterIsVisit(undefined);
                        setFilterVisitResult(undefined);
                        setFilterIsTemu(undefined);
                        setFilterIsExpected(undefined);
                    }}
                >
                    重置
                </Button>
            </div>
            <Table<PickupReservationDetailItem>
                rowKey="reservationNo"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                scroll={{ x: 3200 }}
                pagination={{
                    current: currentPage,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: handlePageChange,
                    showTotal: (t2) => `共 ${t2} 条`,
                }}
                size="small"
            />
            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.pickupReservationDetail}
            />
        </Drawer>
    );
};

export default PickupReservationDrawer;
